import { HTTP_METHODS } from "@src/constants/http-methods.constant";
import { INTERNAL_API_ENDPOINTS } from "@src/constants/internal-api-endpoints-constants";
import { callApi } from "@src/utils/api-client";
import ClientLogsService from "./client-logs.service";

export type FeatureBootstrapRole = "patient" | "referrer" | "patient navigator";

export interface FeatureBootstrapRequest {
  user_uuid: string;
  role: FeatureBootstrapRole;
}

export interface FeatureBootstrapManifest {
  assignments: Record<string, string>;
  transactionID?: string;
}

const CONTROL_ONLY_MANIFEST: FeatureBootstrapManifest = Object.freeze({ assignments: {} });
// The Next.js proxy accepts FEATURE_BOOTSTRAP_TIMEOUT_MS up to 10 seconds.
// Keep the browser deadline slightly longer so the proxy can return its
// bounded result before the client degrades to an empty manifest.
const DEFAULT_BOOTSTRAP_TIMEOUT_MS = 11_000;

const isStringRecord = (value: unknown): value is Record<string, string> =>
  typeof value === "object" &&
  value !== null &&
  !Array.isArray(value) &&
  Object.values(value).every((entry) => typeof entry === "string");

export const parseFeatureBootstrapManifest = (value: unknown): FeatureBootstrapManifest | null => {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return null;
  }

  const candidate = value as Record<string, unknown>;
  if (!isStringRecord(candidate.assignments) || typeof candidate.transactionID !== "string") {
    return null;
  }

  return {
    assignments: candidate.assignments,
    transactionID: candidate.transactionID,
  };
};

export async function bootstrapFeatureAssignments(
  request: FeatureBootstrapRequest,
  timeoutMs = DEFAULT_BOOTSTRAP_TIMEOUT_MS,
): Promise<FeatureBootstrapManifest> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const { data, response } = await callApi({
      method: HTTP_METHODS.POST,
      endpoint: INTERNAL_API_ENDPOINTS.FEATURE_BOOTSTRAP,
      body: { ...request },
      signal: controller.signal,
    });
    const manifest = response.ok ? parseFeatureBootstrapManifest(data) : null;

    if (manifest) {
      return manifest;
    }

    ClientLogsService.logError(
      request.user_uuid,
      "feature-bootstrap.service.bootstrapFeatureAssignments",
      response.ok ? "Feature bootstrap returned an invalid manifest" : "Feature bootstrap failed",
    );
    return CONTROL_ONLY_MANIFEST;
  } catch (error) {
    ClientLogsService.logError(
      request.user_uuid,
      "feature-bootstrap.service.bootstrapFeatureAssignments",
      error instanceof Error && error.name === "AbortError"
        ? "Feature bootstrap timed out"
        : "Feature bootstrap request failed",
      error instanceof Error ? error : new Error("Unknown feature bootstrap error"),
    );
    return CONTROL_ONLY_MANIFEST;
  } finally {
    clearTimeout(timeout);
  }
}
